// store.js
import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate'
import { fetchCities, resolveCity } from '@/services/cities'

// In-flight cities request, deduplicating concurrent loadCities() calls within
// a single page load. Deliberately module-level rather than store state, so it
// is never written to sessionStorage.
let citiesRequest = null;

// Cities are master data that an admin can change at any time, so the cached
// list is deliberately NOT persisted: every page load starts empty and
// re-fetches GET /cities. Everything else keeps its previous persistence.
const CITY_CACHE_KEYS = ['cities', 'citiesLoading', 'citiesError', 'citiesLoadedAt'];

export default createStore({
  plugins: [createPersistedState({
    storage: sessionStorage,
    reducer: (state) => {
      const persisted = { ...state };
      CITY_CACHE_KEYS.forEach(key => delete persisted[key]);
      return persisted;
    },
  })],
  state: {
    patientDetails: null,
    patientid: localStorage.getItem('patientid') || null,
    selectedPatientId: null,
    user: null,
    appoitmentDraft: '{}',
    clinicDetails: {
      clinicId: null,
      clinicname: "",
      doctorId: null,
      doctorImage:"",
      // shown in the clinic console's navigation
      doctorName: "",
      specialization: ""
    },
    userpool: null,
    // Cities master, loaded once from GET /cities and shared by the search
    // box, the doctor forms, the patient profile and the admin screen.
    cities: [],
    citiesLoading: false,
    citiesError: null,
    citiesLoadedAt: null,
  },
  mutations: {
    setAuthUser(state, user) {
      state.user = JSON.stringify(user);
    },
    setPatientDetails(state, details) {
      state.patientDetails = details;
    },
    clearPatientDetails(state) {
      state.patientDetails = null;
    },
    setPatientId(state, patientid) {
      state.patientid = patientid;
      localStorage.setItem('patientid', patientid);
    },
    setSelectedPatientId(state, patientid) { 
      state.selectedPatientId = patientid;
    },
    setAppointmentDraft(state, data) {
      let appmDraft;
      try {
        appmDraft = state.appoitmentDraft ? JSON.parse(state.appoitmentDraft) : {};
      } catch {
        appmDraft = {};
      }
      if(data.doctorid) appmDraft.doctorid = data.doctorid;
      if(data.patientid) appmDraft.patientid = data.patientid;
      if(data.appointmentby) appmDraft.appointmentby = data.appointmentby;
      if(data.day) appmDraft.day = data.day;
      if(data.time) appmDraft.time = data.time;
      if(data.clinic) appmDraft.clinic = data.clinic;
      if(data.clinicname) appmDraft.clinicname = data.clinicname;
      if(data.reason) appmDraft.reason = data.reason;
      if(data.age) appmDraft.age = data.age;
      if(data.doctorImage) appmDraft.doctorImage = data.doctorImage;
      state.appoitmentDraft = JSON.stringify(appmDraft);
    },
    clearAppointmentDraft(state) {
      state.appoitmentDraft = '{}';
    },
    setClinicDetails(state, { clinicId, clinicname, doctorId, doctorImage, doctorName = '', specialization = '' }) {
      state.clinicDetails = { clinicId, clinicname, doctorId, doctorImage, doctorName, specialization };
    },
    setUserPool(state, userpool) {  // Mutation for setting userpool
      state.userpool = userpool;
    },
    setCities(state, cities) {
      state.cities = cities;
      state.citiesLoadedAt = Date.now();
      state.citiesError = null;
    },
    setCitiesLoading(state, loading) {
      state.citiesLoading = loading;
    },
    setCitiesError(state, message) {
      state.citiesError = message;
    },
    upsertCity(state, city) {
      if (!city || !city.city_id) return;
      const index = state.cities.findIndex(c => c.city_id === city.city_id);
      if (index === -1) {
        state.cities = [...state.cities, city].sort((a, b) => a.name.localeCompare(b.name));
      } else {
        state.cities.splice(index, 1, city);
      }
    },
  },
  actions: {
    updatePatientDetails({ commit }, details) {
      commit('setPatientDetails', details);
    },
    resetPatientDetails({ commit }, details) {
      commit('clearPatientDetails');
    },
    updatePatientId({ commit }, patientid) {
      commit('setPatientId', patientid);
    },
    updateSelectedPatientId({ commit }, patientid) { 
      commit('setSelectedPatientId', patientid);
    },
    updateAppointmentDraft({ commit }, data) {
      commit('setAppointmentDraft', data);
    },
    resetAppointmentDraft({ commit }, data) {
      commit('clearAppointmentDraft');
    },
    storeClinicDetails({ commit }, details) {
      commit('setClinicDetails', details);
    },
    updateUserPool({ commit }, userpool) {  
      commit('setUserPool', userpool);
    },
    // Fetches the active cities once per page load, reusing the result across
    // components. Pass { force: true } after an admin edit to refresh
    // immediately. Never throws - callers read state.citiesError.
    async loadCities({ commit, state }, { force = false } = {}) {
      if (!force && state.citiesLoadedAt && state.cities.length) return state.cities;
      if (citiesRequest) return citiesRequest;

      commit('setCitiesLoading', true);
      commit('setCitiesError', null);
      citiesRequest = (async () => {
        try {
          const cities = await fetchCities();
          commit('setCities', cities);
          return cities;
        } catch (error) {
          console.error('Error loading cities:', error);
          commit('setCitiesError', 'Unable to load cities. Please try again.');
          return state.cities;
        } finally {
          commit('setCitiesLoading', false);
          citiesRequest = null;
        }
      })();
      return citiesRequest;
    },
    // Resolves a manually entered city name to a master record. The backend
    // returns the existing city when one matches, so no duplicate is created.
    async resolveCityByName({ commit }, payload) {
      const city = await resolveCity(payload);
      commit('upsertCity', city);
      return city;
    },
  },
  getters: {
    isLoggedIn(state) {
      return state.user ? true : false
    },
    getPatientDetails: (state) => { 
      state.patientDetails;
     },
    getMobileNumber: (state) => {
      return state.patientDetails ? state.patientDetails.phone : null;
    },
    getEmail: (state) => {
      return state.patientDetails ? state.patientDetails.email : null;
    },

    getfirstName: (state) => {
      return state.patientDetails ? state.patientDetails.firstName : null;
    },
    getlastName: (state) => {
      return state.patientDetails ? state.patientDetails.lastName : null;
    },
    getGender: (state) => {
      return state.patientDetails ? state.patientDetails.gender : null;
    },
    getPatientId: (state) => {
      return state.patientid;
    },
    getSelectedPatientId(state) { 
      return state.selectedPatientId;
    },
    getAppointmentDraft: (state) => {
      try {
        return state.appoitmentDraft ? JSON.parse(state.appoitmentDraft) : {};
      } catch {
        return {};
      }
    },
    getclinicDetails(state) { 
      return state.clinicDetails;
    },
    getUserPool(state) {
      return state.userpool;
    },
    getCities(state) {
      return state.cities;
    },
    getCityNames(state) {
      return state.cities.map(city => city.name);
    },
    isCitiesLoading(state) {
      return state.citiesLoading;
    },
    getCitiesError(state) {
      return state.citiesError;
    },
    getUserTokens: (state) => {
      if (state.user) {
        try {
          const currUser = JSON.parse(state.user);
          return currUser.tokens;
        } catch(err) {
          state.user = null;
          return null;
        }
      }
    }
  }
});
